import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';

const CONTACT_EMAIL = 'info@cevop.com';

type Status = 'idle' | 'submitting' | 'sent' | 'error';

function ContactForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/v1/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message, website }),
      });
      const payload = (await response.json().catch(() => null)) as
        | { data?: { received?: boolean }; error?: { message?: string } }
        | null;
      if (!response.ok || !payload?.data?.received) {
        setStatus('error');
        setError(payload?.error?.message ?? 'We could not send your message. Try again in a moment.');
        return;
      }
      setStatus('sent');
    } catch {
      setStatus('error');
      setError('We could not send your message. Check your connection and try again.');
    }
  }

  const field = 'mt-1 w-full rounded-xl border border-line bg-panel px-3 py-2.5 text-[0.9rem] text-ink outline-none focus:border-brand';

  if (status === 'sent') {
    return (
      <div className="mb-16 rounded-2xl border border-line bg-panel p-6 shadow-sm lg:p-8">
        <h2 className="font-display mb-1 text-[1.15rem] font-bold text-ink">Message sent</h2>
        <p className="text-[0.82rem] leading-[1.65] text-ink-3">
          Thanks{name ? `, ${name}` : ''}. We received your message and sent an acknowledgement to {email}. We will
          reply there.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="mb-16 rounded-2xl border border-line bg-panel p-6 shadow-sm lg:p-8">
      <h2 className="font-display mb-1 text-[1.15rem] font-bold text-ink">Write to us</h2>
      <p className="mb-6 text-[0.82rem] leading-[1.65] text-ink-3">
        Send a message and we will email you an acknowledgement. You can also write to{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-brand hover:underline">
          {CONTACT_EMAIL}
        </a>
        .
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-[0.78rem] font-semibold text-ink-2">
          Name
          <input className={field} value={name} onChange={(e) => setName(e.target.value)} name="name" autoComplete="name" required />
        </label>
        <label className="text-[0.78rem] font-semibold text-ink-2">
          Email
          <input
            className={field}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            name="email"
            type="email"
            autoComplete="email"
            required
          />
        </label>
      </div>
      <label className="sr-only" aria-hidden="true">
        Website
        <input
          className={field}
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          name="website"
          tabIndex={-1}
          autoComplete="off"
        />
      </label>
      <label className="mt-4 block text-[0.78rem] font-semibold text-ink-2">
        Message
        <textarea
          className={`${field} min-h-[140px]`}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          name="message"
          required
        />
      </label>
      {error ? <p className="mt-4 text-[0.82rem] text-red">{error}</p> : null}
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-5 rounded-xl bg-brand px-6 py-3 text-[0.875rem] font-semibold text-brand-on hover:bg-brand-hover disabled:opacity-60"
      >
        {status === 'submitting' ? 'Sending…' : 'Send message'}
      </button>
    </form>
  );
}

export function ContactPage() {
  return (
    <section className="px-6 pt-28 pb-16 lg:px-10 lg:pt-36 lg:pb-20">
      <div className="mx-auto max-w-[1280px]">
        <div className="mb-14 max-w-[540px]">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-brand/15 bg-brand-light px-3 py-1.5 text-[0.72rem] font-bold tracking-widest text-brand uppercase">
            Contact
          </div>
          <h1 className="font-display mb-5 text-[2.75rem] leading-[1.06] font-extrabold text-ink lg:text-[3.375rem]">
            Get in touch.
          </h1>
          <p className="text-[1.0625rem] leading-[1.7] text-ink-2">
            Questions about a plan, support, or anything else — send a message and we will reply by email.
          </p>
        </div>

        <ContactForm />

        <div className="grid items-center gap-6 rounded-2xl bg-inverse p-7 lg:grid-cols-[1fr_auto] lg:p-10">
          <div>
            <div className="font-display mb-2 text-[1.2rem] font-bold text-inverse-fg">Looking for a quick answer?</div>
            <p className="text-[0.85rem] leading-[1.65] text-inverse-fg/50">
              Many questions are already answered in the help centre.
            </p>
          </div>
          <Link
            to="/help"
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-xl bg-inverse-fg px-6 py-3 text-[0.875rem] font-semibold whitespace-nowrap text-inverse hover:bg-inverse-fg/90"
          >
            Visit help centre
          </Link>
        </div>
      </div>
    </section>
  );
}
